import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Briefcase, Receipt, ShieldCheck, Truck, Zap,
  TrendingDown, Clock, Calculator, Award, CheckCircle2, MapPin, MessageCircle
} from "lucide-react";
import Logo from "@/components/Logo";
import PublicRateCalculator from "@/components/PublicRateCalculator";
import { formatCHF } from "@/lib/format";
import DemoRequestModal from "@/components/DemoRequestModal";
import { SERVICE_OPERATOR, informationUrl } from "@/lib/contact";
import { api } from "@/lib/api";
import { validPriceTax, formatVatRate, estimateTaxFromNet } from "@/lib/priceTax";

const HERO_IMG = "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1920&q=80";

const PLAN_IDS = ["free", "business_start", "business_pro", "enterprise"];
const money = value => Math.round((value + Number.EPSILON) * 100) / 100;

function readBusinessCatalog(planData, catalogData) {
  const plans = {};
  for (const id of PLAN_IDS) {
    const plan = planData?.plans?.find(item => item.id === id);
    const tax = plan?.price_tax;
    const valid = plan && validPriceTax(tax) && tax.audience === "business"
      && Number.isInteger(plan.included_deliveries_monthly) && plan.included_deliveries_monthly >= 0
      && Number.isFinite(plan.included_km_per_delivery) && plan.included_km_per_delivery >= 0;
    if (!valid) throw new Error("Incomplete business catalog");
    plans[id] = { ...plan, price: tax.net_chf, included: plan.included_deliveries_monthly,
      km: plan.included_km_per_delivery, blurb: plan.target || (id === "free" ? "Nessun canone. Paghi solo le consegne che usi." : "Per le consegne ricorrenti della tua attività") };
  }
  const standard = catalogData?.delivery_types?.find(item => item.id === "standard");
  const medium = catalogData?.package_sizes?.find(item => item.id === "medium");
  if (![standard?.base_price, standard?.price_per_km, standard?.minimum_price, medium?.multiplier].every(value => Number.isFinite(value) && value >= 0)
      || medium.multiplier <= 0
      || new Set(Object.values(plans).map(plan => plan.price_tax.vat_rate)).size !== 1) {
    throw new Error("Incomplete Standard pricing");
  }
  return { plans, pricing: { ...standard, packageMultiplier: medium.multiplier } };
}

/**
 * Phase 43.36 — Calcola il costo totale mensile DapNow per un dato piano,
 * dati i parametri del business (consegne, km medi). Restituisce { total, includedExtra, overSub }.
 */
function computePlanCost(plan, md, avgKm, pricing) {
  const pkgMul = pricing.packageMultiplier;
  const includedUsed = Math.min(md, plan.included);
  const extraDeliveries = Math.max(0, md - plan.included);
  const grossStandard = money(Math.max(pricing.minimum_price, (pricing.base_price + avgKm * pricing.price_per_km) * pkgMul));
  const extraKmPerIncluded = Math.max(0, avgKm - plan.km);
  const includedExtraCost = money(extraKmPerIncluded * pricing.price_per_km * pkgMul);
  const includedExtraTotal = money(includedUsed * includedExtraCost);
  const overSubscriptionCost = money(extraDeliveries * grossStandard);
  return {
    total: money(plan.price + includedExtraTotal + overSubscriptionCost),
    includedUsed,
    extraDeliveries,
    includedExtraTotal,
    overSubscriptionCost,
    grossStandard,
  };
}

/**
 * Phase 43.36 — Confronta TUTTI i piani e suggerisce quello con costo totale
 * mensile più basso, includendo sempre il pagamento a consegna Free.
 * Restituisce { plan, reason } dove reason spiega la scelta (es. "miglior prezzo").
 */
function recommendPlan(md, avgKm, plans, pricing) {
  const candidates = PLAN_IDS.map(id => plans[id]).map((p) => ({
    plan: p,
    cost: computePlanCost(p, md, avgKm, pricing).total,
  }));
  // Ordina per costo crescente
  candidates.sort((a, b) => a.cost - b.cost);
  const winner = candidates[0];
  const reason = winner.plan.id === "free" ? "il pagamento a consegna ha il costo stimato più basso" : "costo totale stimato più basso tra i piani confrontati";
  return { plan: winner.plan, reason };
}

function pricePerIncluded(plan) {
  if (!plan.included) return 0;
  return plan.price / plan.included;
}

export default function B2BLanding() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoPlan, setDemoPlan] = useState("");
  const openDemo = (plan) => { setDemoPlan(plan || ""); setDemoOpen(true); };
  const [catalog, setCatalog] = useState({ status: "loading", plans: null, pricing: null });

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/subscriptions/plans", { params: { audience: "business" } }),
      api.get("/catalog"),
    ]).then(([plans, pricing]) => {
      const result = readBusinessCatalog(plans.data, pricing.data);
      if (active) setCatalog({ status: "ready", ...result });
    }).catch(() => { if (active) setCatalog({ status: "error", plans: null, pricing: null }); });
    return () => { active = false; };
  }, []);

  const [inputs, setInputs] = useState({
    monthly_deliveries: 25,
    avg_distance_km: 6,
    current_cost_per_delivery: 18,
    minutes_per_delivery_internal: 25,
    hourly_cost_staff: 35,
  });

  const num = (v) => (Number.isFinite(+v) ? +v : 0);
  const set = (k) => (e) => {
    const limits = { monthly_deliveries: [0, 300], avg_distance_km: [1, 50], current_cost_per_delivery: [0, 100000], minutes_per_delivery_internal: [0, 1440], hourly_cost_staff: [0, 100000] };
    const [min, max] = limits[k];
    const value = e.target.value === "" ? "" : Math.min(max, Math.max(min, num(e.target.value)));
    setInputs(s => ({ ...s, [k]: k === "monthly_deliveries" && value !== "" ? Math.floor(value) : value }));
  };

  const calc = useMemo(() => {
    if (catalog.status !== "ready") return null;
    const md = Math.max(0, Math.min(300, Math.floor(num(inputs.monthly_deliveries))));
    const avgKm = Math.max(1, Math.min(50, num(inputs.avg_distance_km)));
    const cur = Math.max(0, num(inputs.current_cost_per_delivery));
    const mins = Math.max(0, num(inputs.minutes_per_delivery_internal));
    const hc = Math.max(0, num(inputs.hourly_cost_staff));

    // 1) Costo attuale stimato
    const current_monthly_cost = md * cur;

    // 2) Tempo risparmiato (interno)
    const minutes_total = md * mins;
    const hours_saved = minutes_total / 60;
    const time_value = hours_saved * hc;

    // 3) Phase 43.36 — Calcoliamo il costo per OGNI piano e scegliamo il migliore
    const reco = recommendPlan(md, avgKm, catalog.plans, catalog.pricing);
    const plan = reco.plan;
    const planCost = computePlanCost(plan, md, avgKm, catalog.pricing);
    const dapnow_total_monthly = planCost.total;
    const effectiveAvgPerDelivery = md > 0 ? dapnow_total_monthly / md : 0;

    // Il tempo liberato è un beneficio distinto, non uno sconto o risparmio monetario.
    const monthly_saving = money(current_monthly_cost - dapnow_total_monthly);
    const annual_saving = monthly_saving * 12;

    return {
      md, avgKm, cur, mins, hc,
      current_monthly_cost,
      hours_saved,
      time_value,
      plan,
      plan_reason: reco.reason,
      includedUsed: planCost.includedUsed,
      extraDeliveries: planCost.extraDeliveries,
      includedExtraTotal: planCost.includedExtraTotal,
      overSubscriptionCost: planCost.overSubscriptionCost,
      grossStandard: planCost.grossStandard,
      effectiveAvgPerDelivery,
      dapnow_total_monthly,
      monthly_saving,
      annual_saving,
      tax: estimateTaxFromNet(dapnow_total_monthly, plan.price_tax.vat_rate),
      is_saving: monthly_saving > 0,
    };
  }, [inputs, catalog]);

  return (
    <div className="min-h-screen bg-white break-words" data-testid="b2b-landing">
      {/* Top bar — Phase 43.39: composizione premium, logo più grande, switch segmentato Privati/Aziende */}
      <header className="absolute top-0 inset-x-0 z-30 pt-[env(safe-area-inset-top)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-3 pb-2 md:py-0 md:min-h-28 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4">
          <Logo variant="light" size="lg" />
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Switch segmentato Privati/Aziende — desktop */}
            <div className="hidden md:inline-flex items-center rounded-full border border-white/15 bg-white/5 backdrop-blur p-1">
              <Link
                to="/"
                className="px-4 h-9 inline-flex items-center rounded-full text-[13px] font-semibold text-white/75 hover:text-white transition"
              >
                Per privati
              </Link>
              <span className="px-4 h-9 inline-flex items-center rounded-full text-[13px] font-semibold bg-white text-dapnow-ink" aria-current="page">
                Per aziende
              </span>
            </div>
            <Link
              to="/login?audience=business"
              data-testid="b2b-login-link"
              className="inline-flex h-10 sm:h-11 px-3 sm:px-4 items-center rounded-xl text-[13px] sm:text-sm font-semibold text-white/90 hover:text-white"
            >
              Accedi
            </Link>
            <Link
              to="/register?audience=business"
              data-testid="b2b-header-register-btn"
              className="inline-flex h-10 sm:h-11 px-3.5 sm:px-5 items-center rounded-xl bg-white text-dapnow-ink text-[13px] sm:text-sm font-semibold hover:bg-white/90 transition shadow-sm"
            >
              <span className="sm:hidden">Inizia</span>
              <span className="hidden sm:inline">Inizia ora</span>
              <ArrowRight className="ml-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Link>
          </div>
        </div>
        {/* Switch segmentato — mobile, centrato sotto l'header */}
        <div className="md:hidden flex justify-center px-4 pt-1">
          <div className="inline-flex items-center rounded-full border border-white/15 bg-white/5 backdrop-blur p-0.5" data-testid="b2b-segment-mobile">
            <Link
              to="/"
              data-testid="b2b-private-link-mobile"
              className="px-3.5 h-7 inline-flex items-center rounded-full text-[11px] font-semibold text-white/75 hover:text-white"
            >
              Per privati
            </Link>
            <span className="px-3.5 h-7 inline-flex items-center rounded-full text-[11px] font-semibold bg-white text-dapnow-ink" aria-current="page">
              Per aziende
            </span>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-dapnow-ink text-white" data-testid="b2b-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/55 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-56 pb-14 sm:pt-60 md:pt-48 sm:pb-20">
          <p className="overline text-white/70" data-testid="b2b-tagline">DapNow Business · Ticino e Grigioni italiano</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
            Consegne in giornata.<br />
            <span className="text-dapnow-blue">Per la tua attività.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-white/75 leading-relaxed" data-testid="b2b-hero-intro">
            Consegne per aziende e negozi con il team DapNow, 7 giorni su 7.
            Standard: ritiro entro le 18:00, consegna entro le 21:00. Ritiri serali con tempi da concordare.
          </p>
          <p className="mt-3 text-sm text-white/75" data-testid="b2b-price-basis">Prezzi IVA esclusa. IVA e totale nel preventivo.</p>

          <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <a href="#calcola-prezzo" data-testid="b2b-price-cta" className="btn-primary w-full sm:w-auto">
              <Calculator className="mr-2 h-4 w-4" /> Calcola il prezzo
            </a>
            <Link to="/register?audience=business" data-testid="b2b-hero-register-btn" className="btn-ghost w-full sm:w-auto bg-white/10 border-white/20 text-white hover:bg-white/20">
              Inizia ora <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </div>
          <nav aria-label="Esplora DapNow Business" className="mt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-3" data-testid="b2b-section-navigation">
            <a href="#roi" data-testid="b2b-roi-cta" className="min-w-0 min-h-11 inline-flex items-center justify-center rounded-xl border border-white/25 px-3 py-2.5 text-sm text-center font-semibold text-white hover:bg-white/10">
              Calcola risparmio
            </a>
            <a href="#piani-business" data-testid="b2b-plans-cta" className="min-w-0 min-h-11 inline-flex items-center justify-center rounded-xl border border-white/25 px-3 py-2.5 text-sm text-center font-semibold text-white hover:bg-white/10">
              Piani business
            </a>
            <a
              href={informationUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="b2b-hero-whatsapp-cta"
              className="col-span-2 min-h-11 inline-flex items-center justify-center sm:justify-start px-3 py-2.5 text-sm font-semibold text-white/85 hover:text-white"
            >
              <MessageCircle className="mr-2 h-4 w-4" /> Parla con noi
            </a>
          </nav>
        </div>
      </section>

      <div id="calcola-prezzo" className="scroll-mt-6">
        <PublicRateCalculator audience="business" />
      </div>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20" data-testid="business-account-options">
        <p className="overline text-dapnow-blue">Per la tua attività</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
          Un account per aziende e negozi.
        </h2>
        <p className="mt-3 max-w-2xl text-dapnow-muted">
          Stessi servizi di consegna, stessa area servita e stesse regole per i pacchi.
          Scegli il tipo di account della tua attività per iniziare.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {[
            { icon: Building2, role: "customer_business", title: "Azienda", description: "Organizza consegne tra sedi, collaboratori e clienti." },
            { icon: Briefcase, role: "customer_shop", title: "Negozio", description: "Spedisci prodotti e pacchi ai tuoi clienti locali." },
          ].map(({ icon: Icon, role, title, description }) => (
            <div key={role} className="card-soft p-6">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{title}</h3>
              <p className="mt-1 text-sm text-dapnow-muted">{description}</p>
              <Link to={`/register?audience=business&role=${role}`} data-testid={`b2b-register-${role}`} className="inline-flex mt-4 text-sm font-semibold text-dapnow-blue hover:underline">
                Crea account {title.toLowerCase()} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Why B2B */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20">
        <p className="overline text-dapnow-blue">Perché DapNow Business</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">
          Tre cose che cambiano davvero per la tua attività.
        </h2>
        <div className="mt-10 grid sm:grid-cols-3 gap-5">
          <ValueCard icon={TrendingDown} title="Pacchetto incluso"
            desc="Un canone fisso con consegne locali incluse ogni mese. Sai prima quanto spendi e riduci le consegne gestite caso per caso." />
          <ValueCard icon={Clock} title="Standard · Consegne in giornata"
            desc="La flotta interna gestisce le spedizioni Standard in Ticino e nei Grigioni italiano, secondo tratta e disponibilità da confermare." />
          <ValueCard icon={ShieldCheck} title="Tracciabilità"
            desc="Stato consegna live, notifiche operative, storico ordini e dashboard centralizzata per il team." />
        </div>
      </section>

      {/* ROI Calculator */}
      <section id="roi" aria-labelledby="roi-title" className="scroll-mt-6 bg-dapnow-bg border-y border-dapnow-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue text-white">
              <Calculator className="h-5 w-5" />
            </span>
            <p className="overline text-dapnow-blue">Calcolatore risparmio</p>
          </div>
          <h2 id="roi-title" className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">
            Calcola risparmio
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-dapnow-muted">
            Confrontiamo i costi di consegna al netto dell’IVA. Il valore del tempo liberato è mostrato separatamente e non è un risparmio monetario garantito.
            <span className="block mt-2 text-xs text-dapnow-muted">Stima per pacchi Medium, servizio Standard e ritiri diurni nei giorni non festivi. Supplementi, tratta reale e disponibilità vanno verificati nel calcolatore prezzi.</span>
            <span className="block mt-1 text-[11px] text-dapnow-muted/80 italic">Stima indicativa, non offerta vincolante.</span>
          </p>

          {calc ? (
          <div className="mt-10 grid lg:grid-cols-5 gap-6">
            {/* INPUTS */}
            <div className="min-w-0 lg:col-span-2 card-soft p-5 sm:p-6 space-y-5" data-testid="roi-inputs">
              <SliderInput
                label="Consegne mensili"
                testid="roi-monthly-deliveries"
                value={inputs.monthly_deliveries}
                onChange={set("monthly_deliveries")}
                min={0} max={300} step={1} suffix="consegne / mese"
              />
              <SliderInput
                label="Distanza media per consegna"
                testid="roi-avg-distance"
                value={inputs.avg_distance_km}
                onChange={set("avg_distance_km")}
                min={1} max={50} step={1} suffix="km"
              />
              <NumberInput
                label="Costo medio attuale per consegna, IVA esclusa"
                testid="roi-cost-per-delivery"
                value={inputs.current_cost_per_delivery}
                onChange={set("current_cost_per_delivery")}
                suffix="CHF"
                hint="Costo monetario della consegna: indica un importo al netto dell’IVA"
              />
              <NumberInput
                label="Minuti interni liberabili per consegna"
                testid="roi-minutes-internal"
                value={inputs.minutes_per_delivery_internal}
                onChange={set("minutes_per_delivery_internal")}
                suffix="min"
                hint="Spostamento, parcheggio, firma, ritorno…"
              />
              <NumberInput
                label="Costo orario del personale"
                testid="roi-hourly-cost"
                value={inputs.hourly_cost_staff}
                onChange={set("hourly_cost_staff")}
                suffix="CHF/h"
                hint="Stipendio + oneri / ore lavorate. Questo costo interno è considerato per intero."
              />
            </div>

            {/* RESULTS */}
            <div className="min-w-0 lg:col-span-3 space-y-5" data-testid="roi-results">
              {/* Highlight: saving */}
              <div className={`rounded-3xl p-7 text-white shadow-xl ${calc.is_saving ? "bg-gradient-to-br from-emerald-600 to-emerald-800" : "bg-gradient-to-br from-dapnow-ink to-zinc-700"}`}>
                <p className="overline text-white/80 flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5" /> {calc.monthly_saving >= 0 ? "Risparmio monetario stimato" : "Maggior costo monetario stimato"} <span className="text-[10px] text-white/65 normal-case tracking-normal">(IVA esclusa, senza valore del tempo)</span>
                </p>
                <div className="mt-2 grid sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-white/70">Mensile</p>
                    <p className="font-display text-3xl font-extrabold tracking-tighter mt-0.5" data-testid="roi-monthly-saving">
                      {formatCHF(Math.abs(calc.monthly_saving))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-white/70">Annuo</p>
                    <p className="font-display text-3xl font-extrabold tracking-tighter mt-0.5" data-testid="roi-annual-saving">
                      {formatCHF(Math.abs(calc.annual_saving))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-white/70">Ore liberabili / mese</p>
                    <p className="font-display text-3xl font-extrabold tracking-tighter mt-0.5" data-testid="roi-hours-saved">
                      {calc.hours_saved.toFixed(1)} h
                    </p>
                  </div>
                </div>
                {calc.monthly_saving < 0 && (
                  <p className="mt-4 text-xs text-white/80">
                    Con i parametri inseriti DapNow costa più dell’alternativa attuale. Il possibile tempo liberato è un beneficio distinto, da valutare per la tua attività.
                  </p>
                )}
              </div>

              {/* Breakdown grid */}
              <div className="grid sm:grid-cols-2 gap-5">
                <ResultBlock title="Costo attuale stimato, IVA esclusa" testid="roi-current-block">
                  <Row k="Consegne / mese" v={`${calc.md}`} />
                  <Row k="Costo medio per consegna" v={formatCHF(calc.cur)} />
                  <Row k="Costo mensile attuale" v={formatCHF(calc.current_monthly_cost)} bold />
                </ResultBlock>

                <ResultBlock title="Valore del tempo, separato dai costi" testid="roi-time-block">
                  <Row k="Minuti per consegna" v={`${calc.mins} min`} />
                  <Row k="Costo orario personale" v={`${formatCHF(calc.hc)}/h`} />
                  <Row k="Ore liberabili / mese" v={`${calc.hours_saved.toFixed(1)} h`} />
                  <Row k="Valore potenziale del tempo" v={formatCHF(calc.time_value)} bold accent />
                  <p className="pt-2 text-xs text-dapnow-muted">Non viene sommato al risparmio monetario: non implica una riduzione effettiva del costo del personale.</p>
                </ResultBlock>

                <ResultBlock title="Costo stimato con DapNow" testid="roi-dapnow-block">
                  <Row k={`Abbonamento ${calc.plan.name}`} v={formatCHF(calc.plan.price)} />
                  <Row k="Consegne incluse usate" v={`${calc.includedUsed} / ${calc.plan.included || 0}`} />
                  {calc.includedExtraTotal > 0 && (
                    <Row k={`Km extra (oltre i ${calc.plan.km} km)`} v={`+ ${formatCHF(calc.includedExtraTotal)}`} />
                  )}
                  {calc.extraDeliveries > 0 && (
                    <Row k={`Consegne oltre soglia (×${calc.extraDeliveries} a tariffa standard)`} v={`+ ${formatCHF(calc.overSubscriptionCost)}`} />
                  )}
                  <Row k="Subtotale mensile, IVA esclusa" v={formatCHF(calc.tax.net_chf)} bold />
                  <Row k={`IVA ${formatVatRate(calc.tax.vat_rate)}`} v={formatCHF(calc.tax.vat_chf)} />
                  <Row k="Totale mensile, IVA inclusa" v={formatCHF(calc.tax.total_chf)} bold />
                  {calc.md > 0 && (
                    <Row k="Costo medio per consegna, IVA esclusa" v={formatCHF(calc.effectiveAvgPerDelivery)} accent />
                  )}
                </ResultBlock>

                <ResultBlock title="Confronto monetario, IVA esclusa" testid="roi-summary-block">
                  <Row k="Costo attuale delle consegne" v={formatCHF(calc.current_monthly_cost)} />
                  <Row k="Costo con DapNow" v={formatCHF(calc.dapnow_total_monthly)} />
                  <Row k={calc.monthly_saving >= 0 ? "Risparmio mensile stimato" : "Maggior costo mensile stimato"} v={formatCHF(Math.abs(calc.monthly_saving))} bold accent={calc.is_saving} />
                  <Row k={calc.monthly_saving >= 0 ? "Risparmio annuo stimato" : "Maggior costo annuo stimato"} v={formatCHF(Math.abs(calc.annual_saving))} bold accent={calc.is_saving} />
                </ResultBlock>
              </div>

              {/* Plan recommendation */}
              <div className="card-soft p-6 border-2 border-dapnow-blue/30 bg-dapnow-blue-soft" data-testid="roi-plan-recommend">
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="flex-shrink-0">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dapnow-blue text-white">
                      <Award className="h-6 w-6" />
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="overline text-dapnow-blue">Piano consigliato per te</p>
                    <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">{calc.plan.name}</h3>
                    <p className="text-sm text-dapnow-muted mt-1">{calc.plan.blurb}</p>
                    {calc.plan_reason && (
                      <p className="text-[11px] text-dapnow-blue font-semibold uppercase tracking-wider mt-1" data-testid="roi-plan-reason">
                        {calc.plan_reason}
                      </p>
                    )}
                    {calc.md > 0 && calc.plan.included > 0 && (
                      <p className="mt-3 text-sm text-dapnow-ink leading-relaxed" data-testid="roi-plan-narrative">
                        Con <strong>{calc.plan.name}</strong> hai <strong>{calc.plan.included} consegne locali incluse</strong>.
                        Con <strong>{calc.md} consegne/mese</strong> e una distanza media di {calc.avgKm} km, il costo medio stimato è <strong>{formatCHF(calc.effectiveAvgPerDelivery)}/consegna, IVA esclusa</strong>.
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <Bullet>{calc.plan.included > 0 ? `${calc.plan.included} consegne locali incluse/mese` : "Pagamento singolo per consegna"}</Bullet>
                      {calc.plan.included > 0 && <Bullet>Fino a {calc.plan.km} km cad. · Standard; Shared in arrivo</Bullet>}
                      <Bullet>Riepilogo mensile automatico</Bullet>
                      {calc.plan.id !== "free" && <Bullet>Priorità nell'assegnazione driver</Bullet>}
                      {calc.plan.id === "enterprise" && <Bullet>Condizioni dedicate da concordare</Bullet>}
                    </div>
                    <div className="mt-5">
                      {/* Phase 41 — un solo CTA, stessa logica della PlanCard:
                          - Business Start/Pro → registrazione diretta con plan preselected
                          - Enterprise → demo/contatto
                          - Free → registrazione semplice */}
                      {calc.plan.id === "enterprise" ? (
                        <button
                          type="button"
                          onClick={() => openDemo(calc.plan.id)}
                          data-testid="roi-plan-cta"
                          className="btn-primary"
                        >
                          Richiedi una demo <ArrowRight className="ml-1.5 h-4 w-4" />
                        </button>
                      ) : (
                        <Link
                          to={`/register?audience=business${
                            calc.plan.id === "business_start"
                              ? "&plan=start"
                              : calc.plan.id === "business_pro"
                                ? "&plan=pro"
                                : "&plan=free"
                          }`}
                          data-testid="roi-plan-cta"
                          className="btn-primary"
                        >
                          {calc.plan.id === "business_start"
                            ? "Attiva Business Start"
                            : calc.plan.id === "business_pro"
                              ? "Attiva Business Pro"
                              : "Inizia gratis"}
                          <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="overline">Da</p>
                    <p className="font-display text-3xl font-extrabold tracking-tighter">{formatCHF(calc.plan.price)}</p>
                    <p className="text-xs text-dapnow-muted">/ mese · IVA esclusa</p>
                    <p className="mt-2 text-xs text-dapnow-muted">IVA {formatVatRate(calc.plan.price_tax.vat_rate)}: {formatCHF(calc.plan.price_tax.vat_chf)}</p>
                    <p className="text-xs font-semibold">Totale {formatCHF(calc.plan.price_tax.total_chf)} / mese</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ) : <p role="status" className="mt-6 text-dapnow-muted" data-testid="business-catalog-status">{catalog.status === "loading" ? "Caricamento delle tariffe aggiornate…" : "Tariffe aggiornate non disponibili. Riprova più tardi: la stima del risparmio è sospesa."}</p>}
        </div>
      </section>

      {/* Plans summary */}
      <section id="piani-business" aria-labelledby="business-plans-title" className="scroll-mt-6 mx-auto max-w-6xl px-4 sm:px-6 py-20">
        <p className="overline text-dapnow-blue">Per le consegne ricorrenti</p>
        <h2 id="business-plans-title" className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">
          Piani business
        </h2>
        <p className="mt-3 text-sm text-dapnow-muted">Canoni IVA esclusa. Ogni piano riporta anche IVA e totale mensile.</p>
        {catalog.plans ? <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PlanCard plan={catalog.plans.free} onCta={() => openDemo("unsure")} />
          <PlanCard plan={catalog.plans.business_start} accent onCta={() => openDemo("business_start")} />
          <PlanCard plan={catalog.plans.business_pro} popular onCta={() => openDemo("business_pro")} />
          <PlanCard plan={catalog.plans.enterprise} dark onCta={() => openDemo("enterprise")} />
        </div> : <p className="mt-6 text-sm text-dapnow-muted">I piani saranno mostrati quando il catalogo aggiornato sarà disponibile.</p>}
        <p className="mt-6 text-xs text-dapnow-muted leading-relaxed max-w-3xl" data-testid="plans-disclaimer">
          Le consegne incluse coprono <strong>Standard</strong> entro la distanza indicata dal piano, esclusivamente in <strong>Ticino e Grigioni italiano</strong>.
          <strong> Shared</strong> resta tra i servizi previsti dai piani esistenti, ma è <strong>in arrivo e non ancora prenotabile</strong>: è il futuro servizio per driver privati.
          Per tratte superiori si paga solo il supplemento km extra. Per gli altri servizi, seleziona il tipo di consegna nel calcolatore per consultare tempi, condizioni e verifica preventiva. Gli eventuali supplementi seguono le condizioni del piano; il preventivo non accetta la prenotazione.
          <strong> Van</strong> è su richiesta, con verifica preventiva: massimo 25 kg e 120 × 80 × 80 cm per collo, imballaggio incluso; prezzo dedicato, non incluso. Oltre il numero mensile incluso le consegne sono fatturate alla tariffa standard.
        </p>

        {/* Phase 43.36 — Riepilogo mensile, prudente: non promettere "fattura PDF" se non ancora live */}
        <div className="mt-6 rounded-2xl border border-dapnow-line bg-dapnow-bg/50 p-5 max-w-3xl" data-testid="monthly-billing-clarification">
          <p className="overline text-emerald-700">Riepilogo mensile automatico</p>
          <h3 className="mt-2 font-display text-lg font-semibold tracking-tight">Canone e supplementi sotto controllo.</h3>
          <p className="mt-2 text-sm text-dapnow-muted leading-relaxed">
            Prima dell’acquisto puoi verificare canone, IVA e totale. I piani mensili prevedono rinnovo automatico;
            consegne extra e supplementi sono indicati separatamente. L’attivazione richiede conferma del pagamento.
          </p>
        </div>
      </section>

      {/* Use cases */}
      <section className="bg-dapnow-bg border-y border-dapnow-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20">
          <p className="overline text-dapnow-blue">Casi d'uso</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Per chi è pensato DapNow Business</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <UseCase icon={Building2} title="Uffici e studi"
              desc="Documenti, contratti, plichi tra sedi e consegne ai clienti, senza far uscire un collaboratore." />
            <UseCase icon={Briefcase} title="Negozi e e-commerce"
              desc="Consegne in giornata ai tuoi clienti locali, verificando disponibilità e orario per ciascuna spedizione." />
            <UseCase icon={Truck} title="Aziende con magazzino"
              desc="Trasferimenti tra sedi, ricambi urgenti e consegne ai cantieri, con servizio adatto alla dimensione e all'urgenza." />
            <UseCase icon={Receipt} title="Studi medici e farmacie"
              desc="Consegne tracciate di documenti, materiali leggeri e articoli non soggetti a gestione speciale. Per prodotti regolamentati si applicano condizioni dedicate." />
            <UseCase icon={Zap} title="Ristoranti e catering"
              desc="Catering aziendali, materiali per eventi, piccoli allestimenti e consegne programmate." />
            <UseCase icon={MapPin} title="Cantieri e B2B locali"
              desc="Piccoli ricambi, materiali leggeri e attrezzi urgenti, con ritiro rapido dove disponibile." />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20 text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
          Pronto a liberare tempo<br />
          <span className="text-dapnow-blue">e ridurre i costi?</span>
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-dapnow-muted">
          Crea il tuo account business gratuito o richiedi una demo: ti mostriamo come integrare DapNow nei tuoi flussi di consegna.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link
            to="/register?audience=business"
            data-testid="final-cta-register"
            className="btn-primary"
          >
            Inizia ora <ArrowRight className="ml-1.5 h-4 w-4" />
          </Link>
          <button type="button" onClick={() => openDemo()} data-testid="final-cta-demo" className="btn-ghost">
            Richiedi una demo
          </button>
          <a
            href={informationUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-whatsapp"
            className="btn-ghost"
          >
            <MessageCircle className="mr-1.5 h-4 w-4" /> Parla con noi
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dapnow-ink text-white/70">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 flex flex-col sm:flex-row flex-wrap justify-between gap-6 text-sm">
          <Logo variant="light" size="sm" />
          <div className="flex flex-wrap gap-5">
            <Link to="/legal" className="hover:underline">Gestore e contatti</Link>
            <Link to="/" className="hover:text-white">Per privati</Link>
            <Link to="/login?audience=business" className="hover:text-white">Accedi</Link>
            <Link to="/become-driver" className="hover:text-white">Driver privati · In arrivo</Link>
          </div>
          <p>© {new Date().getFullYear()} {SERVICE_OPERATOR}.</p>
        </div>
      </footer>

      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} defaultPlan={demoPlan} />
    </div>
  );
}

function ValueCard({ icon: Icon, title, desc }) {
  return (
    <div className="card-soft p-6">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dapnow-blue-soft text-dapnow-blue">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-dapnow-muted leading-relaxed">{desc}</p>
    </div>
  );
}

function SliderInput({ label, value, onChange, min, max, step, suffix, testid }) {
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <label className="text-sm font-semibold tracking-tight">{label}</label>
        <span className="font-display text-xl font-bold tracking-tight" data-testid={`${testid}-value`}>{value} <span className="text-xs text-dapnow-muted font-medium">{suffix}</span></span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="mt-2 w-full accent-dapnow-blue"
      />
      <input
        type="number"
        min={min} max={max}
        value={value}
        onChange={onChange}
        data-testid={`${testid}-num`}
        className="mt-2 w-full rounded-xl border border-dapnow-line px-3 py-2 text-sm focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none"
      />
    </div>
  );
}

function NumberInput({ label, value, onChange, suffix, hint, testid }) {
  return (
    <div>
      <label className="text-sm font-semibold tracking-tight">{label}</label>
      <div className="mt-1.5 relative">
        <input
          type="number"
          min={0}
          value={value}
          onChange={onChange}
          data-testid={testid}
          className="w-full rounded-xl border border-dapnow-line px-3 py-2.5 text-sm pr-16 focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-dapnow-muted">{suffix}</span>
      </div>
      {hint && <p className="text-xs text-dapnow-muted mt-1">{hint}</p>}
    </div>
  );
}

function ResultBlock({ title, children, testid }) {
  return (
    <div className="min-w-0 card-soft p-5" data-testid={testid}>
      <p className="overline">{title}</p>
      <div className="mt-3 space-y-1.5 text-sm">{children}</div>
    </div>
  );
}

function Row({ k, v, bold, accent }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
      <span className="min-w-0 text-dapnow-muted">{k}</span>
      <span className={`text-right ${bold ? "font-display font-bold" : "font-medium"} ${accent ? "text-emerald-700" : ""}`}>{v}</span>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-dapnow-ink">
      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {children}
    </span>
  );
}

function PlanCard({ plan, popular, dark, accent, onCta }) {
  const isFree = plan.id === "free";
  const perDelivery = pricePerIncluded(plan);
  return (
    <div data-testid={`plan-card-${plan.id}`} className={`min-w-0 relative rounded-3xl p-6 ${dark ? "bg-dapnow-ink text-white" : "bg-white border border-dapnow-line"} ${popular ? "ring-2 ring-dapnow-blue shadow-lg" : ""}`}>
      {popular && (
        <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-dapnow-blue text-white px-3 py-0.5 text-[11px] font-semibold">
          Consegne frequenti
        </span>
      )}
      <p className={`overline ${dark ? "text-white/70" : "text-dapnow-blue"}`}>{plan.name}</p>
      <p className="mt-2 font-display text-3xl font-extrabold tracking-tighter">
        {plan.price > 0 ? formatCHF(plan.price) : "Gratis"}
        {plan.price > 0 && <span className={`text-xs font-medium ${dark ? "text-white/70" : "text-dapnow-muted"}`}> / mese</span>}
      </p>
      <p className={`mt-2 text-xs ${dark ? "text-white/80" : "text-dapnow-muted"}`}>
        {isFree ? "Nessun canone mensile" : "Canone mensile IVA esclusa"}
      </p>
      {!isFree && <div className={`mt-3 space-y-1 text-xs ${dark ? "text-white/80" : "text-dapnow-muted"}`} data-testid={`plan-tax-${plan.id}`}>
        <p>IVA {formatVatRate(plan.price_tax.vat_rate)}: {formatCHF(plan.price_tax.vat_chf)}</p>
        <p className={`font-semibold ${dark ? "text-white" : "text-dapnow-ink"}`}>Totale {formatCHF(plan.price_tax.total_chf)} / mese, IVA inclusa</p>
      </div>}
      <p className={`text-xs mt-1 ${dark ? "text-white/70" : "text-dapnow-muted"}`}>{plan.blurb}</p>
      {plan.included > 0 && (
        <div className={`mt-4 rounded-xl px-3 py-2 text-xs ${dark ? "bg-white/10" : "bg-dapnow-blue-soft"}`}>
          <strong>Pari a {formatCHF(perDelivery)}</strong> per consegna locale inclusa, IVA esclusa
        </div>
      )}
      <ul className={`mt-4 space-y-2 text-sm ${dark ? "text-white/85" : ""}`}>
        {plan.included > 0 ? (
          <>
            <li>• <strong>{plan.included} consegne locali incluse</strong>/mese</li>
            <li>• Fino a {plan.km} km per consegna</li>
            <li>• Standard incluso; Shared previsto dal piano, in arrivo</li>
            <li>• Oltre {plan.km} km: solo i km extra fatturati</li>
            <li>• Altri servizi: condizioni e supplementi nel calcolatore</li>
            <li>• Van su richiesta: verifica preventiva, max 25 kg per collo; prezzo dedicato (non incluso)</li>
            <li>• Riepilogo mensile automatico</li>
          </>
        ) : (
          <>
            {/* Phase 43.37 — Piano Free riformulato: niente "fatturazione mensile" che confonde */}
            <li>• Tariffe standard</li>
            <li>• Pagamento singolo per consegna</li>
            <li>• Nessun pacchetto incluso</li>
            <li>• Riepilogo ordini disponibile</li>
          </>
        )}
        {plan.id === "business_pro" && <li>• Dashboard avanzata + report mensile + utenti multipli</li>}
        {plan.id === "business_start" && <li>• Dashboard business + priorità leggera</li>}
        {plan.id !== "free" && <li>• Priorità nell'assegnazione driver</li>}
        {plan.id === "enterprise" && <li>• Account manager dedicato + condizioni dedicate</li>}
      </ul>
      {isFree ? (
        <Link
          to="/register?audience=business&plan=free"
          data-testid={`plan-card-cta-${plan.id}`}
          className={`mt-6 w-full min-h-11 inline-flex px-3 py-2.5 items-center justify-center rounded-xl text-sm text-center font-semibold ${dark ? "bg-white text-dapnow-ink hover:bg-white/90" : "bg-dapnow-blue text-white hover:bg-dapnow-blue/90"} transition`}
        >
          Inizia gratis <ArrowRight className="ml-1.5 h-4 w-4" />
        </Link>
      ) : plan.id === "enterprise" ? (
        // Phase 41 — Enterprise resta lead/demo (volumi importanti, condizioni custom)
        <button
          type="button"
          onClick={onCta}
          data-testid={`plan-card-cta-${plan.id}`}
          className={`mt-6 w-full min-h-11 inline-flex px-3 py-2.5 items-center justify-center rounded-xl text-sm text-center font-semibold ${dark ? "bg-white text-dapnow-ink hover:bg-white/90" : "bg-dapnow-blue text-white hover:bg-dapnow-blue/90"} transition`}
        >
          Richiedi una demo <ArrowRight className="ml-1.5 h-4 w-4" />
        </button>
      ) : (
        // Phase 41 — Business Start / Pro: registrazione diretta con plan preselected
        <Link
          to={`/register?audience=business&plan=${plan.id === "business_start" ? "start" : "pro"}`}
          data-testid={`plan-card-cta-${plan.id}`}
          className={`mt-6 w-full min-h-11 inline-flex px-3 py-2.5 items-center justify-center rounded-xl text-sm text-center font-semibold ${dark ? "bg-white text-dapnow-ink hover:bg-white/90" : "bg-dapnow-blue text-white hover:bg-dapnow-blue/90"} transition`}
        >
          {plan.id === "business_start" ? "Attiva Business Start" : "Attiva Business Pro"}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function UseCase({ icon: Icon, title, desc }) {
  return (
    <div className="card-soft p-5">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-3 font-display text-lg font-bold tracking-tight">{title}</h3>
      <p className="text-sm text-dapnow-muted mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}
